import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, X, ChevronDown, ShieldCheck, Sparkles, CalendarDays, Ruler, ReceiptText, PiggyBank, Bell, ArrowRight, Tag, BookOpen, MessagesSquare, Trophy, Video, ListChecks } from "lucide-react";
import { createPortal } from "react-dom";
import { trackEvent } from "@/lib/analytics";
import {
  PLANOS,
  BENEFICIOS,
  FAQ_CLUBE,
  TRIAL_DIAS,
  VENDAS_ABERTAS,
  FUNDADORAS_ABERTAS,
  DATA_LANCAMENTO,
  VAGAS_FUNDADORAS,
  NOME_TURMA,
  ECONOMIA_MES,
  ECONOMIA_SEMANA,
  LOGOS_MERCADO,
  checkoutUrl,
  type PlanoId,
} from "@/data/clube";
import ListaVipForm from "@/components/ListaVipForm";
import kitHeroBg from "@/assets/kit-hero-bg.webp";
import marmitas from "@/assets/quiz/clube-marmitas.webp";
import testimonial1 from "@/assets/testimonial-1.jpeg";
import testimonial2 from "@/assets/testimonial-2.jpeg";
import testimonial3 from "@/assets/testimonial-3.jpeg";
import testimonial4 from "@/assets/testimonial-4.jpeg";
import testimonial5 from "@/assets/testimonial-5.jpeg";
import capaMercado from "@/assets/capa-mercado-aus.avif";
import capaWhey from "@/assets/capa-whey.avif";
import capaMolhos from "@/assets/capa-molhos.avif";
import capaOrganizar from "@/assets/capa-organizar.avif";
import capaRefeicaoLivre from "@/assets/capa-refeicao-livre.avif";
import capaCruzeiro from "@/assets/capa-cruzeiro.avif";

const claraPortrait = "/clara-portrait-final.png";

export const faqItems = FAQ_CLUBE;

/* ===== Paleta da LP (herdada da LP do Kit Vida Saudável) ===== */
const ACCENT = "hsl(150 22% 35%)";
const ACCENT_LIGHT = "hsl(150 22% 40%)";
const ACCENT_GLOW = "hsl(150 22% 35% / 0.15)";
const BG_PAGE = "hsl(32 35% 97%)";
const BG_ALT = "hsl(30 24% 93%)";
const BG_CARD = "hsl(0 0% 100%)";
const BORDER = "hsl(32 20% 88%)";
const BORDER_ACCENT = "hsl(150 22% 35% / 0.25)";
const TEXT_H = "hsl(30 10% 18%)";
const TEXT_B = "hsl(30 10% 33%)";
const TEXT_M = "hsl(30 10% 50%)";
const GRADIENT = `linear-gradient(100deg, hsl(150 25% 32%), ${ACCENT}, ${ACCENT_LIGHT}, hsl(155 30% 50%))`;
const SOMBRA_CARD = `0 2px 12px hsl(30 10% 80% / 0.2)`;
const SOMBRA_DESTAQUE = `0 12px 40px hsl(30 10% 70% / 0.22), 0 0 24px ${ACCENT_GLOW}`;

const CTA_TEXT = VENDAS_ABERTAS ? `Quero ${TRIAL_DIAS} dias grátis` : "Quero minha vaga";
const MICRO_CTA = VENDAS_ABERTAS
  ? `${TRIAL_DIAS} dias grátis · cancela em 1 clique · A$ ${PLANOS.mensal.preco}/mês depois`
  : `${NOME_TURMA} · ${VAGAS_FUNDADORAS} vagas · A$ ${PLANOS.fundadora.preco}/mês para sempre · abre em ${DATA_LANCAMENTO}`;

const irCheckout = (plano: PlanoId, origem: string) => {
  trackEvent("clube_cta_checkout", { plano, origem });
  window.location.href = checkoutUrl(plano, { origem });
};

const EVENTO_VAGA = "clube:abrir-vaga";

const irParaVaga = (origem: string) => {
  trackEvent("clube_cta_lista", { origem });
  window.dispatchEvent(new CustomEvent(EVENTO_VAGA, { detail: origem }));
};

/** Ação principal da página: checkout quando as vendas estão abertas, lista VIP antes disso. */
const acaoPrincipal = (origem: string, plano: PlanoId = "mensal") => (VENDAS_ABERTAS ? irCheckout(plano, origem) : irParaVaga(origem));

const CTAButton = ({ origem, label = CTA_TEXT, className = "", plano = "mensal" }: { origem: string; label?: string; className?: string; plano?: PlanoId }) => (
  <button
    type="button"
    onClick={() => acaoPrincipal(origem, plano)}
    className={`block w-full max-w-xl rounded-2xl border-0 py-5 text-center text-lg font-extrabold uppercase tracking-wide text-white transition-all hover:scale-[1.02] active:scale-[0.99] sm:text-xl font-sans ${className}`}
    style={{ background: GRADIENT, boxShadow: `0 8px 28px ${ACCENT_GLOW}` }}
  >
    {label}
  </button>
);

const Selo = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[12px] font-bold uppercase tracking-[0.2em] font-sans" style={{ color: ACCENT }}>
    {children}
  </p>
);

const Titulo = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <h2 className={`text-[1.75rem] font-extrabold leading-[1.1] tracking-[-0.02em] sm:text-4xl lg:text-5xl font-sans ${className}`} style={{ color: TEXT_H }}>
    {children}
  </h2>
);

const Linha = () => <div className="mx-auto h-1 w-16 rounded-full" style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_LIGHT})` }} />;

const Cabecalho = ({ selo, titulo, texto }: { selo: string; titulo: React.ReactNode; texto?: string }) => (
  <div className="mx-auto max-w-2xl space-y-4 text-center">
    <Selo>{selo}</Selo>
    <Titulo>{titulo}</Titulo>
    <Linha />
    {texto && (
      <p className="text-base leading-relaxed sm:text-lg" style={{ color: TEXT_B }}>
        {texto}
      </p>
    )}
  </div>
);

/* ===== Mockup do cardápio que chega no celular ===== */
const MockCardapio = () => (
  <div className="mx-auto w-full max-w-[330px] rounded-[2.4rem] border-[6px] border-[hsl(30_10%_18%)] bg-[hsl(30_10%_18%)] p-1 shadow-[0_30px_60px_-30px_rgba(0,0,0,.45)]">
    <div className="overflow-hidden rounded-[2rem] bg-white font-sans">
      <div className="px-5 pb-4 pt-6" style={{ background: GRADIENT }}>
        <div className="flex items-center justify-between text-white/80">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Clube Braussie</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold">
            <Bell className="h-3 w-3" /> Quarta, 7h
          </span>
        </div>
        <p className="mt-2 text-[18px] font-extrabold leading-tight text-white">Seu cardápio da semana</p>
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-white/85">
          <img src={LOGOS_MERCADO.Coles} alt="Coles" className="h-3.5 w-auto rounded bg-white px-1 py-0.5" />
          <span>Ofertas de 15 a 21 de out · 1 pessoa · nas suas medidas</span>
        </div>
      </div>
      <div className="divide-y px-5" style={{ borderColor: BORDER }}>
        {[
          ["Seg", "Frango desfiado, arroz, feijão e legumes", "Coxa de frango 1/2 preço"],
          ["Ter", "Carne moída com abobrinha e arroz", "Mince em oferta"],
          ["Qua", "Omelete de 2 ovos com salada", "Ovos 18 un. em oferta"],
        ].map(([d, r, o]) => (
          <div key={d} className="flex items-start gap-3 py-3">
            <span className="mt-0.5 w-8 flex-shrink-0 rounded-md py-0.5 text-center text-[10px] font-bold" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
              {d}
            </span>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold" style={{ color: TEXT_H }}>
                {r}
              </p>
              <p className="text-[11px]" style={{ color: TEXT_M }}>
                {o}
              </p>
            </div>
          </div>
        ))}
        <div className="py-3 text-[11px]" style={{ color: TEXT_M }}>
          + quinta a domingo, café, lanche e jantar
        </div>
      </div>
      <div className="m-3 rounded-2xl p-4" style={{ background: BG_PAGE }}>
        <div className="flex items-center justify-between text-[12px]" style={{ color: TEXT_B }}>
          <span>Lista: 23 itens por corredor</span>
          <span className="font-extrabold" style={{ color: TEXT_H }}>
            A$ 61
          </span>
        </div>
        <div className="mt-2 flex items-center justify-between rounded-xl px-3 py-2 text-[12px] font-bold text-white" style={{ background: GRADIENT }}>
          <span>Você economizou com 4 ofertas</span>
          <span>A$ 19</span>
        </div>
      </div>
    </div>
  </div>
);

/* ===== Mockup navegável da plataforma ===== */
const ABAS = ["Cardápio", "Ofertas", "Lista", "Guias", "Comunidade"] as const;
type Aba = (typeof ABAS)[number];

const GUIAS_MOCK = [
  ["Mercado australiano", capaMercado],
  ["Whey e suplementos", capaWhey],
  ["Molhos e temperos", capaMolhos],
  ["Organizar a semana", capaOrganizar],
  ["Refeição livre", capaRefeicaoLivre],
  ["Cruzeiro e viagem", capaCruzeiro],
] as const;

const Logo = ({ m }: { m: string }) => (
  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border bg-white p-1" style={{ borderColor: BORDER }}>
    <img src={LOGOS_MERCADO[m]} alt={m} className="h-full w-full object-contain" />
  </span>
);

const ConteudoAba = ({ aba }: { aba: Aba }) => {
  const sub = "text-[11px] font-bold uppercase tracking-wider";
  if (aba === "Cardápio")
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-1">
          <Logo m="Coles" />
          <p className={sub} style={{ color: TEXT_M }}>
            15 a 21 de out · 1 pessoa · suas medidas
          </p>
        </div>
        {[
          ["Seg", "Frango desfiado, arroz, feijão e legumes", "Coxa de frango 1/2 preço"],
          ["Ter", "Carne moída com abobrinha e arroz", "Mince em oferta"],
          ["Qua", "Omelete de 2 ovos com salada", "Ovos 18 un. em oferta"],
          ["Qui", "Sobras do frango em wrap com folhas", "Rende do segunda"],
        ].map(([d, r, o]) => (
          <div key={d} className="flex items-center gap-3 rounded-2xl border px-3 py-2.5" style={{ borderColor: BORDER }}>
            <span className="w-9 flex-shrink-0 rounded-md py-1 text-center text-[11px] font-bold" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
              {d}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold" style={{ color: TEXT_H }}>
                {r}
              </p>
              <p className="text-[11px]" style={{ color: TEXT_M }}>
                {o}
              </p>
            </div>
            <span className="flex-shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold" style={{ borderColor: BORDER, color: TEXT_B }}>
              Trocar
            </span>
          </div>
        ))}
        <p className="px-1 text-[11px]" style={{ color: TEXT_M }}>
          + sexta a domingo, café, lanche e jantar. Porções calculadas para o seu perfil.
        </p>
      </div>
    );
  if (aba === "Ofertas")
    return (
      <div className="space-y-2">
        <p className={`px-1 ${sub}`} style={{ color: TEXT_M }}>
          Ofertas desta semana · todos os mercados
        </p>
        {[
          ["Coles", "Coxa de frango", "1/2 preço · A$ 6/kg", "Vale", true],
          ["Woolworths", "Iogurte grego 1 kg", "40% off · A$ 5,40", "Vale", true],
          ["Aldi", "Arroz 5 kg", "A$ 8,49 · preço base", "Vale", true],
          ["Coles", "Barra de cereal", "2 por A$ 6", "Não vale", false],
        ].map(([m, p, pr, nota, ok]) => (
          <div key={String(p)} className="flex items-center gap-3 rounded-2xl border px-3 py-2.5" style={{ borderColor: BORDER }}>
            <Logo m={String(m)} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold" style={{ color: TEXT_H }}>
                {p}
              </p>
              <p className="text-[11px]" style={{ color: TEXT_M }}>
                {pr}
              </p>
            </div>
            <span className="flex-shrink-0 rounded-full px-2 py-1 text-[10px] font-bold" style={ok ? { background: `hsl(150 22% 35% / 0.12)`, color: ACCENT } : { background: "hsl(0 60% 95%)", color: "hsl(0 55% 40%)" }}>
              {nota}
            </span>
          </div>
        ))}
        <p className="px-1 text-[11px] italic" style={{ color: TEXT_M }}>
          "Não vale: açúcar, troca por fruta." Nota da Clara em cada item.
        </p>
      </div>
    );
  if (aba === "Lista")
    return (
      <div className="space-y-3">
        {(
          [
            ["Açougue", [["Coxa de frango 1 kg", "A$ 6,00", true], ["Carne moída 500 g", "A$ 7,50", true]]],
            ["Hortifruti", [["Abobrinha 2 un.", "A$ 2,40", true], ["Tomate 500 g", "A$ 3,50", false]]],
            ["Mercearia", [["Arroz 5 kg", "A$ 8,49", false], ["Feijão preto 1 kg", "A$ 3,20", false]]],
            ["Laticínios", [["Ovos 18 un.", "A$ 6,50", false]]],
          ] as [string, [string, string, boolean][]][]
        ).map(([corredor, itens]) => (
          <div key={corredor}>
            <p className={`mb-1.5 px-1 ${sub}`} style={{ color: TEXT_M }}>
              {corredor}
            </p>
            <div className="divide-y rounded-2xl border" style={{ borderColor: BORDER }}>
              {itens.map(([item, preco, feito]) => (
                <div key={item} className="flex items-center gap-3 px-3 py-2">
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border" style={feito ? { background: ACCENT, borderColor: ACCENT, color: "#fff" } : { borderColor: "hsl(30 10% 75%)" }}>
                    {feito && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>
                  <span className={`flex-1 text-[13px] ${feito ? "line-through" : ""}`} style={{ color: feito ? TEXT_M : TEXT_H }}>
                    {item}
                  </span>
                  <span className="text-[12px] font-semibold" style={{ color: TEXT_B }}>
                    {preco}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between rounded-2xl px-4 py-3 text-white" style={{ background: GRADIENT }}>
          <span className="text-[12px] font-semibold">23 itens · total A$ 61 · economia A$ 19</span>
          <ReceiptText className="h-4 w-4" />
        </div>
      </div>
    );
  if (aba === "Guias")
    return (
      <div className="space-y-2">
        <p className={`px-1 ${sub}`} style={{ color: TEXT_M }}>
          14 guias práticos · 6 em destaque
        </p>
        <div className="grid grid-cols-3 gap-2">
          {GUIAS_MOCK.map(([t, img]) => (
            <div key={t} className="overflow-hidden rounded-xl border" style={{ borderColor: BORDER }}>
              <img src={img} alt="" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <p className="px-2 py-1.5 text-[10px] font-semibold leading-tight" style={{ color: TEXT_H }}>
                {t}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 rounded-2xl px-3 py-2" style={{ background: `hsl(150 22% 35% / 0.08)` }}>
        <span className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: ACCENT }}>
          <Trophy className="h-3.5 w-3.5" /> Desafio de Verão · dia 9 de 21
        </span>
        <span className="text-[11px] font-semibold" style={{ color: TEXT_B }}>
          Check-in 9/9
        </span>
      </div>
      {(
        [
          ["Mari · Gold Coast", "Compra da semana no Aldi deu A$ 58, com as 21 refeições. 🙌", false],
          ["Clara", "Boa, Mari! Se sobrou coxa de frango, congela em porções que rende mais uma semana.", true],
          ["Ju · Sydney", "O feijão preto do Coles em oferta é bom? Nunca comprei essa marca.", false],
          ["Clara", "É bom, Ju. Deixa de molho de véspera que fica igual ao de casa.", true],
        ] as [string, string, boolean][]
      ).map(([quem, msg, clara], i) => (
        <div key={i} className={`flex items-end gap-2 ${clara ? "flex-row-reverse" : ""}`}>
          {clara ? <img src={claraPortrait} alt="" className="h-7 w-7 flex-shrink-0 rounded-full object-cover object-top" /> : <span className="h-7 w-7 flex-shrink-0 rounded-full" style={{ background: "hsl(30 20% 85%)" }} />}
          <div className="max-w-[82%] rounded-2xl px-3 py-2" style={clara ? { background: `hsl(150 22% 35% / 0.12)` } : { background: BG_PAGE }}>
            <p className="text-[10px] font-bold" style={{ color: clara ? ACCENT : TEXT_M }}>
              {quem}
            </p>
            <p className="text-[12px] leading-snug" style={{ color: TEXT_H }}>
              {msg}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const MockPlataforma = () => {
  const [aba, setAba] = useState<Aba>("Ofertas");
  return (
    <div className="w-full min-w-0 overflow-hidden rounded-3xl border bg-white p-2 font-sans" style={{ borderColor: BORDER, boxShadow: SOMBRA_DESTAQUE }}>
      <div className="scrollbar-hide flex gap-1 overflow-x-auto rounded-2xl p-1" role="tablist" style={{ background: BG_PAGE }}>
        {ABAS.map((t) => (
          <button key={t} type="button" role="tab" aria-selected={aba === t} onClick={() => setAba(t)} className="flex-shrink-0 rounded-xl px-2.5 py-2 text-[11px] font-semibold transition sm:px-3 sm:text-[12px]" style={aba === t ? { background: GRADIENT, color: "#fff" } : { color: TEXT_M }}>
            {t}
          </button>
        ))}
      </div>
      <div className="min-h-[300px] p-3">
        <ConteudoAba aba={aba} />
      </div>
      <p className="px-3 pb-2 text-center text-[11px]" style={{ color: TEXT_M }}>
        Toque nas abas para ver cada parte da plataforma.
      </p>
    </div>
  );
};

/* ===== Folha da lista VIP: sobe de baixo no celular, diálogo no PC ===== */
const FolhaVaga = ({ aberta, origem, onFechar }: { aberta: boolean; origem: string; onFechar: () => void }) => {
  useEffect(() => {
    if (!aberta) return;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", esc);
    return () => {
      document.body.style.overflow = anterior;
      window.removeEventListener("keydown", esc);
    };
  }, [aberta, onFechar]);
  if (!aberta || typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={`Lista VIP da ${NOME_TURMA}`}>
      <button type="button" aria-label="Fechar" onClick={onFechar} className="absolute inset-0 bg-black/45 animate-in fade-in duration-200" />
      <div
        className="relative w-full max-h-[92dvh] overflow-y-auto rounded-t-[1.75rem] bg-white px-5 pb-6 pt-3 shadow-2xl animate-in slide-in-from-bottom duration-300 sm:max-w-md sm:rounded-3xl sm:p-8 sm:zoom-in-95 sm:slide-in-from-bottom-0"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full sm:hidden" style={{ background: "hsl(30 10% 85%)" }} />
        <button type="button" onClick={onFechar} aria-label="Fechar" className="absolute right-4 top-4 hidden h-9 w-9 items-center justify-center rounded-full sm:flex" style={{ background: BG_ALT, color: TEXT_B }}>
          <X className="h-4 w-4" />
        </button>
        <div className="mb-5 space-y-1.5 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
            <Sparkles className="h-3.5 w-3.5" /> {NOME_TURMA} · {VAGAS_FUNDADORAS} vagas
          </span>
          <h3 className="text-2xl font-extrabold tracking-tight" style={{ color: TEXT_H }}>
            Garanta a sua vaga
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: TEXT_B }}>
            A$ {PLANOS.fundadora.preco} por mês para sempre. O link chega no seu WhatsApp em {DATA_LANCAMENTO}, antes de todo mundo.
          </p>
        </div>
        <ListaVipForm origem={origem} />
      </div>
    </div>,
    document.body,
  );
};

/* ===== Simulador ===== */
const GASTO_SEMANAL: Record<string, number> = { Coles: 95, Woolworths: 95, Aldi: 75, Misturo: 85 };

const Simulador = () => {
  const [mercado, setMercado] = useState("Coles");
  const [pessoas, setPessoas] = useState(1);
  const { gasto, economiaMes, economiaSemana, plano } = useMemo(() => {
    const base = GASTO_SEMANAL[mercado] ?? 90;
    const semanal = Math.round(base * (1 + (pessoas - 1) * 0.75));
    const mensal = Math.round(semanal * 4.33);
    const eMes = Math.round(mensal * 0.18);
    return { gasto: mensal, economiaMes: eMes, economiaSemana: Math.round(eMes / 4.33), plano: pessoas > 1 ? PLANOS.familia : PLANOS.mensal };
  }, [mercado, pessoas]);
  const chip = (ativo: boolean) =>
    ativo ? { borderColor: ACCENT, background: `hsl(150 22% 35% / 0.07)`, color: TEXT_H, boxShadow: `0 0 0 1px ${ACCENT} inset` } : { borderColor: BORDER, background: BG_PAGE, color: TEXT_M };
  const precoClube = VENDAS_ABERTAS ? plano.preco : PLANOS.fundadora.preco;
  const legendaClube = VENDAS_ABERTAS ? `plano ${plano.nome.toLowerCase()}` : NOME_TURMA.toLowerCase();
  return (
    <div className="space-y-6 rounded-3xl border p-5 sm:p-8" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: `0 8px 32px hsl(30 10% 70% / 0.15)` }}>
      <div className="grid gap-5 sm:grid-cols-[1.4fr_1fr]">
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider font-sans" style={{ color: TEXT_M }}>
            Onde você compra
          </p>
          <div className="grid grid-cols-2 gap-2">
            {Object.keys(GASTO_SEMANAL).map((m) => (
              <button key={m} type="button" onClick={() => setMercado(m)} className="flex h-12 items-center justify-center gap-2 rounded-xl border px-2 text-sm font-semibold transition font-sans" style={chip(mercado === m)}>
                {LOGOS_MERCADO[m] && <img src={LOGOS_MERCADO[m]} alt="" className="h-6 w-auto max-w-[64px] object-contain" />}
                {(m === "Woolworths" || !LOGOS_MERCADO[m]) && <span className="truncate">{m === "Misturo" ? "Mix dos três" : m}</span>}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider font-sans" style={{ color: TEXT_M }}>
            Pessoas na casa
          </p>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((n) => (
              <button key={n} type="button" onClick={() => setPessoas(n)} className="h-12 rounded-xl border text-sm font-semibold transition font-sans" style={chip(pessoas === n)}>
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 sm:flex-col sm:justify-center sm:py-5 sm:text-center" style={{ background: BG_PAGE }}>
          <p className="text-[12px] font-semibold uppercase tracking-wide font-sans" style={{ color: TEXT_M }}>
            Mercado por mês hoje
          </p>
          <p className="text-2xl font-extrabold tracking-tight font-sans" style={{ color: TEXT_H }}>
            A$ {gasto}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-white sm:flex-col sm:justify-center sm:py-5 sm:text-center" style={{ background: GRADIENT }}>
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-white/85 font-sans">Sobra por mês</p>
            <p className="text-[11px] text-white/85 font-sans">≈ A$ {economiaSemana} por semana</p>
          </div>
          <p className="text-2xl font-extrabold tracking-tight font-sans">A$ {economiaMes}</p>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 sm:flex-col sm:justify-center sm:py-5 sm:text-center" style={{ background: BG_PAGE }}>
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-wide font-sans" style={{ color: TEXT_M }}>
              O clube custa
            </p>
            <p className="text-[11px] font-sans" style={{ color: TEXT_M }}>
              {legendaClube}
            </p>
          </div>
          <p className="text-2xl font-extrabold tracking-tight font-sans" style={{ color: TEXT_H }}>
            A$ {precoClube}
          </p>
        </div>
      </div>
      <p className="text-center text-xs leading-relaxed font-sans" style={{ color: TEXT_M }}>
        Estimativa com base nas listas das últimas semanas, comprando com as ofertas em vez de comprar no impulso. O número real aparece na sua tela toda quarta.
      </p>
    </div>
  );
};

/* ===== Planos (modo vendas abertas) ===== */
const PlanoCard = ({ id, destaque }: { id: PlanoId; destaque?: boolean }) => {
  const p = PLANOS[id];
  return (
    <div className={`flex flex-col gap-4 rounded-3xl border p-6 ${destaque ? "sm:-translate-y-2" : ""}`} style={{ background: BG_CARD, borderColor: destaque ? BORDER_ACCENT : BORDER, boxShadow: destaque ? SOMBRA_DESTAQUE : SOMBRA_CARD }}>
      <div className="flex items-center justify-between">
        <p className="text-lg font-extrabold tracking-tight font-sans" style={{ color: TEXT_H }}>
          {p.nome}
        </p>
        {p.destaque && (
          <span className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide font-sans" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
            {p.destaque}
          </span>
        )}
      </div>
      <p className="text-4xl font-extrabold tracking-[-0.03em] font-sans" style={{ color: TEXT_H }}>
        A$ {p.preco}
        <span className="text-sm font-normal tracking-normal font-sans" style={{ color: TEXT_M }}>
          {" "}
          /{p.periodo}
        </span>
      </p>
      <p className="flex-1 text-sm leading-relaxed font-sans" style={{ color: TEXT_B }}>
        {p.descricao}
      </p>
      <button type="button" onClick={() => irCheckout(id, `lp_plano_${id}`)} className="w-full rounded-2xl py-4 text-sm font-extrabold uppercase tracking-wide transition hover:scale-[1.02] font-sans" style={destaque ? { background: GRADIENT, color: "#fff", boxShadow: `0 6px 20px ${ACCENT_GLOW}` } : { border: `1px solid ${ACCENT}`, color: ACCENT, background: BG_CARD }}>
        Começar {TRIAL_DIAS} dias grátis
      </button>
      <p className="text-center text-[11px] font-sans" style={{ color: TEXT_M }}>
        Cobrança só no dia {TRIAL_DIAS + 1}. Cancela em um clique.
      </p>
    </div>
  );
};

/* ===== FAQ ===== */
const Faq = () => {
  const [aberta, setAberta] = useState<number | null>(null);
  return (
    <div className="space-y-3">
      {FAQ_CLUBE.map((faq, i) => {
        const open = aberta === i;
        return (
          <button key={faq.question} type="button" className="w-full rounded-2xl border p-5 text-left transition-all duration-300 sm:p-6" style={{ background: BG_CARD, borderColor: open ? BORDER_ACCENT : BORDER, boxShadow: open ? SOMBRA_DESTAQUE : SOMBRA_CARD }} onClick={() => setAberta(open ? null : i)}>
            <div className="flex items-center gap-4">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: `hsl(150 22% 35% / 0.08)` }}>
                <ChevronDown className="h-4 w-4 transition-transform duration-300" style={{ color: ACCENT, transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
              </span>
              <span className="text-base font-bold sm:text-lg font-sans" style={{ color: TEXT_H }}>
                {faq.question}
              </span>
            </div>
            {open && (
              <p className="ml-12 mt-4 text-sm leading-relaxed sm:text-base font-sans" style={{ color: TEXT_B }}>
                {faq.answer}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
};

const VALOR = [
  { icon: CalendarDays, t: "Cardápio da semana", d: "Sete dias de café, almoço, lanche e jantar, com comida nossa, montados nas ofertas que saem toda quarta." },
  { icon: Ruler, t: "Nas suas medidas", d: "Você informa altura, peso, idade e objetivo uma vez. As porções chegam ajustadas para você e para cada pessoa da casa." },
  { icon: ReceiptText, t: "Lista com preço e total", d: "Por corredor, com o produto certo, o preço da semana e o total antes de você sair de casa." },
  { icon: PiggyBank, t: "Economia na tela", d: `Quanto a compra custou e quanto você economizou com as ofertas. Em média A$ ${ECONOMIA_SEMANA} por semana, A$ ${ECONOMIA_MES} por mês.` },
];

const PASSOS = [
  { n: "1", t: "Saem as ofertas", d: "Coles e Woolies trocam as ofertas toda quarta. A Clara olha as três redes e separa o que presta." },
  { n: "2", t: "O cardápio é montado", d: "Em cima das ofertas do mercado onde você compra, com arroz, feijão, proteína e legumes de verdade." },
  { n: "3", t: "Nas suas medidas", d: "As quantidades são calculadas para o seu perfil. No plano família, para cada pessoa da casa." },
  { n: "4", t: "Chega às 7h", d: "Cardápio, lista por corredor com preço e o total no seu celular. Você compra, cozinha e vê a economia." },
];

const PLATAFORMA = [
  { icon: Tag, t: "Promoções de todos os mercados", d: "Uma aba só com tudo o que está em desconto no Coles, no Woolies e no Aldi nesta semana, com a nota da Clara: o que vale e o que não vale." },
  { icon: ListChecks, t: "Cardápio e lista", d: "O cardápio da semana nas suas medidas e a lista por corredor, com preço e total. Troca de prato em um toque." },
  { icon: BookOpen, t: "14 guias práticos", d: "Mercado, whey, molhos, marmitas, refeição livre, cruzeiro, acampamento e mais, para consultar quando precisar." },
  { icon: MessagesSquare, t: "Comunidade no WhatsApp", d: "Gente na mesma situação, a compra da semana de cada um e as dúvidas respondidas." },
  { icon: Trophy, t: "Desafio de 21 dias, todo mês", d: "Roteiro diário, check-in e ranking de constância. O primeiro é o Desafio de Verão." },
  { icon: Video, t: "Live mensal com a Clara", d: "Uma hora por mês para tirar dúvidas ao vivo, gravada para quem não puder." },
];

const PARA_VOCE = [
  "Mora na Austrália e compra no Coles, no Woolies ou no Aldi",
  "Quer comer comida brasileira de verdade sem gastar mais",
  "Cansou de decidir o que comer todo santo dia",
  "Tem pouco tempo: cozinha rápido ou faz marmita no domingo",
  "Quer ver na tela quanto economizou, toda semana",
];

const NAO_PARA_VOCE = ["Quer uma dieta de corte radical, sem arroz e feijão", "Prefere delivery todo dia e não pretende cozinhar nada", "Não quer olhar a lista no celular antes de ir ao mercado"];

const DEPOIMENTOS = [testimonial1, testimonial2, testimonial3, testimonial4, testimonial5];

const BarraFixaMobile = ({ visivel }: { visivel: boolean }) => (
  <div className={`fixed inset-x-0 bottom-0 z-40 border-t px-4 py-2.5 backdrop-blur transition-transform duration-300 lg:hidden ${visivel ? "translate-y-0" : "translate-y-full"}`} style={{ background: "hsl(32 35% 97% / 0.96)", borderColor: BORDER, paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}>
    <div className="mx-auto flex max-w-xl items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[14px] font-extrabold leading-tight font-sans" style={{ color: TEXT_H }}>
          {VENDAS_ABERTAS ? `${TRIAL_DIAS} dias grátis` : NOME_TURMA}
        </p>
        <p className="text-[12px] leading-tight font-sans" style={{ color: TEXT_B }}>
          {VENDAS_ABERTAS ? `Depois A$ ${PLANOS.mensal.preco}/mês` : `A$ ${PLANOS.fundadora.preco}/mês para sempre`}
        </p>
      </div>
      <button type="button" onClick={() => acaoPrincipal("lp_barra")} className="flex-shrink-0 whitespace-nowrap rounded-xl px-5 py-3 text-[13px] font-extrabold uppercase tracking-wide text-white font-sans" style={{ background: GRADIENT, boxShadow: `0 6px 20px ${ACCENT_GLOW}` }}>
        {VENDAS_ABERTAS ? "Começar grátis" : "Garantir vaga"}
      </button>
    </div>
  </div>
);

const Clube = () => {
  const [barra, setBarra] = useState(false);
  const [folha, setFolha] = useState<{ aberta: boolean; origem: string }>({ aberta: false, origem: "lp" });
  const fecharFolha = useCallback(() => setFolha((f) => ({ ...f, aberta: false })), []);
  useEffect(() => {
    const abrir = (e: Event) => setFolha({ aberta: true, origem: `lp_folha_${(e as CustomEvent<string>).detail ?? "lp"}` });
    window.addEventListener(EVENTO_VAGA, abrir);
    if (!VENDAS_ABERTAS && new URLSearchParams(window.location.search).has("vaga")) setFolha({ aberta: true, origem: "lp_link_vaga" });
    return () => window.removeEventListener(EVENTO_VAGA, abrir);
  }, []);
  useEffect(() => {
    const onScroll = () => setBarra(window.scrollY > 560);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const irDiagnostico = () => trackEvent("clube_cta_diagnostico", { origem: "lp" });

  return (
    <div className="min-h-screen overflow-x-hidden font-sans" style={{ background: BG_PAGE, color: TEXT_H }}>
      {/* ========== HERO DESKTOP ========== */}
      <section className="relative hidden min-h-screen items-center overflow-hidden lg:flex" style={{ background: BG_PAGE }}>
        <div className="absolute inset-0">
          <img src={kitHeroBg} alt="" className="h-full w-full object-cover object-right" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${BG_PAGE} 10%, hsl(32 35% 97% / 0.9) 28%, hsl(32 35% 97% / 0.55) 44%, hsl(32 35% 97% / 0.15) 60%, transparent 75%)` }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${BG_PAGE} 2%, transparent 22%)` }} />
        </div>
        <div className="relative z-10 max-w-7xl px-10 py-24 lg:px-20">
          <div className="max-w-2xl space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em]" style={{ borderColor: ACCENT, color: ACCENT }}>
              <img src="/images/flag-brazil.png" alt="" className="h-auto w-5" /> Para brasileiros na Austrália
            </div>
            <h1 className="text-4xl font-extrabold leading-[1.08] tracking-[-0.02em] lg:text-[3.3rem]" style={{ color: TEXT_H }}>
              Seu cardápio da semana nas ofertas do Coles, Woolies ou Aldi, <span className="gradient-text italic">nas suas medidas</span>, toda quarta às 7h.
            </h1>
            <p className="max-w-xl text-xl leading-relaxed" style={{ color: TEXT_B }}>
              Comida brasileira de verdade, lista de compras com o preço de cada item e, em média, <strong style={{ color: TEXT_H }}>A$ {ECONOMIA_MES} a menos por mês no mercado</strong>. Montado por uma nutricionista brasileira que mora aqui.
            </p>
            <div className="space-y-3">
              <CTAButton origem="lp_hero" />
              <p className="max-w-xl text-sm font-semibold tracking-wide" style={{ color: TEXT_M }}>
                {MICRO_CTA}
              </p>
              <Link to="/diagnostico" onClick={irDiagnostico} className="inline-flex items-center gap-1.5 text-sm font-semibold underline-offset-4 hover:underline" style={{ color: ACCENT }}>
                Não sabe se é pra você? Faça o diagnóstico de 3 minutos <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HERO MOBILE ========== */}
      <section className="relative overflow-hidden lg:hidden" style={{ background: BG_PAGE }}>
        <div className="relative h-[34vh] min-h-[240px] overflow-hidden">
          {/* A Clara fica em ~57% da largura da foto: centraliza nela, não na foto. */}
          <img src={kitHeroBg} alt="Clara Camille com os logos do Woolworths, Coles e Aldi" className="absolute top-0 h-full w-auto max-w-none" style={{ left: "50%", transform: "translateX(-57%)" }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${BG_PAGE} 6%, transparent 50%)` }} />
        </div>
        <div className="relative z-10 -mt-10 px-5 pb-12">
          <div className="space-y-5 rounded-3xl border p-6 backdrop-blur-md" style={{ background: "hsl(0 0% 100% / 0.9)", borderColor: BORDER, boxShadow: `0 12px 40px hsl(30 10% 70% / 0.2)` }}>
            <div className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ borderColor: ACCENT, color: ACCENT }}>
              <img src="/images/flag-brazil.png" alt="" className="h-auto w-4" /> Para brasileiros na Austrália
            </div>
            <h1 className="text-[1.7rem] font-extrabold leading-[1.08] tracking-[-0.02em]" style={{ color: TEXT_H }}>
              Seu cardápio da semana nas ofertas do seu mercado, <span className="gradient-text italic">nas suas medidas</span>, toda quarta às 7h.
            </h1>
            <p className="text-base leading-relaxed" style={{ color: TEXT_B }}>
              Comida brasileira de verdade, lista com o preço de cada item e, em média, <strong style={{ color: TEXT_H }}>A$ {ECONOMIA_MES} a menos por mês</strong> no Coles, Woolies ou Aldi.
            </p>
            <div className="space-y-3">
              <CTAButton origem="lp_hero_mobile" />
              <p className="text-center text-[12px] font-semibold leading-snug" style={{ color: TEXT_M }}>
                {MICRO_CTA}
              </p>
              <Link to="/diagnostico" onClick={irDiagnostico} className="block text-center text-sm font-semibold underline underline-offset-4" style={{ color: ACCENT }}>
                Não sabe se é pra você? Faça o diagnóstico de 3 minutos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== O QUE CHEGA TODA QUARTA ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_ALT }}>
        <div className="mx-auto max-w-5xl space-y-10 px-5">
          <Cabecalho
            selo="A promessa"
            titulo={
              <>
                Toda quarta às 7h, no seu celular, <span className="gradient-text italic">a semana resolvida</span>
              </>
            }
            texto="Você para de decidir o que comer, para de comprar no impulso e passa a ver, toda semana, quanto sobrou. Tudo dentro da plataforma do clube."
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALOR.map((v) => (
              <div key={v.t} className="flex flex-col gap-3 rounded-3xl border p-6" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: SOMBRA_CARD }}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: GRADIENT }}>
                  <v.icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h3 className="text-lg font-extrabold leading-tight tracking-tight" style={{ color: TEXT_H }}>
                  {v.t}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: TEXT_B }}>
                  {v.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== COMO FUNCIONA ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_PAGE }}>
        <div className="mx-auto max-w-5xl space-y-12 px-5">
          <Cabecalho
            selo="Como funciona"
            titulo={
              <>
                Da oferta de quarta ao <span className="gradient-text italic">seu prato</span>
              </>
            }
          />
          <ol className="grid gap-4 pt-4 sm:grid-cols-2 lg:grid-cols-4">
            {PASSOS.map((s) => (
              <li key={s.n} className="relative rounded-2xl border p-5 pt-7" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: SOMBRA_CARD }}>
                <span className="absolute -top-4 left-5 flex h-8 w-8 items-center justify-center rounded-full text-sm font-extrabold text-white" style={{ background: GRADIENT }}>
                  {s.n}
                </span>
                <h3 className="text-base font-extrabold leading-tight tracking-tight" style={{ color: TEXT_H }}>
                  {s.t}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed" style={{ color: TEXT_B }}>
                  {s.d}
                </p>
              </li>
            ))}
          </ol>
          <div className="grid min-w-0 items-center gap-10 lg:grid-cols-2">
            <MockCardapio />
            <div className="space-y-5">
              <Selo>O que aparece na sua tela</Selo>
              <h3 className="text-2xl font-extrabold leading-tight tracking-[-0.02em] sm:text-3xl" style={{ color: TEXT_H }}>
                Cardápio, lista e economia, <span className="gradient-text italic">sem você montar nada</span>
              </h3>
              <ul className="space-y-3">
                {["Os sete dias da semana, refeição por refeição, com as ofertas marcadas", "A lista por corredor, com o produto certo e o preço da semana", "O total da compra e quanto você economizou, antes de sair de casa", "Troca em um toque: não gostou de um prato, vem outro nas mesmas ofertas"].map((i) => (
                  <li key={i} className="flex items-start gap-3 text-base leading-relaxed" style={{ color: TEXT_B }}>
                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full" style={{ background: `hsl(150 22% 35% / 0.12)` }}>
                      <Check className="h-3 w-3" style={{ color: ACCENT }} strokeWidth={3} />
                    </span>
                    {i}
                  </li>
                ))}
              </ul>
              <div className="pt-2">
                <CTAButton origem="lp_como_funciona" className="max-w-md" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== DENTRO DA PLATAFORMA ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_ALT }}>
        <div className="mx-auto max-w-5xl space-y-12 px-5">
          <Cabecalho
            selo="A plataforma"
            titulo={
              <>
                Tudo o que está em oferta, <span className="gradient-text italic">em todos os mercados</span>, numa aba só
              </>
            }
            texto="O cardápio é a parte principal, mas a plataforma do clube tem mais. A aba de promoções mostra, toda semana, os produtos em desconto no Coles, no Woolies e no Aldi, com a nota da Clara em cada um."
          />
          <div className="grid min-w-0 items-start gap-8 lg:grid-cols-2">
            <MockPlataforma />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {PLATAFORMA.map((p) => (
                <div key={p.t} className="flex items-start gap-4 rounded-2xl border p-4" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: SOMBRA_CARD }}>
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
                    <p.icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <p className="text-[15px] font-extrabold leading-tight tracking-tight" style={{ color: TEXT_H }}>
                      {p.t}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed" style={{ color: TEXT_B }}>
                      {p.d}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== PARA QUEM É ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_PAGE }}>
        <div className="mx-auto max-w-5xl space-y-10 px-5">
          <Cabecalho
            selo="Para quem é"
            titulo={
              <>
                O clube é para você <span className="gradient-text italic">se…</span>
              </>
            }
          />
          <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
            <div className="rounded-3xl border p-6 sm:p-8" style={{ background: BG_CARD, borderColor: BORDER_ACCENT, boxShadow: SOMBRA_DESTAQUE }}>
              <p className="mb-4 text-[12px] font-bold uppercase tracking-wider" style={{ color: ACCENT }}>
                É para você se
              </p>
              <ul className="space-y-3">
                {PARA_VOCE.map((i) => (
                  <li key={i} className="flex items-start gap-3 text-base leading-relaxed" style={{ color: TEXT_B }}>
                    <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-white" style={{ background: GRADIENT }}>
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border p-6 sm:p-8" style={{ background: BG_ALT, borderColor: BORDER }}>
              <p className="mb-4 text-[12px] font-bold uppercase tracking-wider" style={{ color: TEXT_M }}>
                Não é para você se
              </p>
              <ul className="space-y-3">
                {NAO_PARA_VOCE.map((i) => (
                  <li key={i} className="flex items-start gap-3 text-base leading-relaxed" style={{ color: TEXT_B }}>
                    <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full" style={{ background: "hsl(30 10% 80%)", color: TEXT_H }}>
                      <X className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {i}
                  </li>
                ))}
                <li className="flex items-start gap-3 text-base leading-relaxed" style={{ color: TEXT_B }}>
                  <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full" style={{ background: "hsl(30 10% 80%)", color: TEXT_H }}>
                    <X className="h-3.5 w-3.5" strokeWidth={3} />
                  </span>
                  <span>
                    Precisa de acompanhamento individual (gestação, diabetes, medicação). Nesse caso, o caminho é a{" "}
                    <Link to="/consulta-inicial-gratuita" className="font-semibold underline underline-offset-4" style={{ color: ACCENT }}>
                      Sessão Clareza gratuita
                    </Link>
                    .
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== A CONTA ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_ALT }}>
        <div className="mx-auto max-w-3xl space-y-8 px-5">
          <Cabecalho
            selo="A conta"
            titulo={
              <>
                Quanto <span className="gradient-text italic">sobra</span> no seu mês
              </>
            }
            texto="Escolha onde você compra e quantas pessoas comem em casa. A economia vem de comprar com o cardápio nas ofertas, em vez de comprar no impulso."
          />
          <Simulador />
        </div>
      </section>

      {/* ========== QUEM É A CLARA ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_PAGE }}>
        <div className="mx-auto max-w-5xl space-y-10 px-5">
          <div className="overflow-hidden rounded-3xl border" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: `0 8px 32px hsl(30 10% 70% / 0.15)` }}>
            <div className="flex flex-col lg:flex-row">
              <div className="relative h-80 flex-shrink-0 overflow-hidden lg:h-auto lg:w-[340px]">
                <img src={claraPortrait} alt="Clara Camille" className="h-full w-full object-cover object-top" loading="lazy" />
                <div className="absolute inset-0 hidden lg:block" style={{ background: `linear-gradient(to right, transparent 60%, ${BG_CARD})` }} />
                <div className="absolute inset-0 lg:hidden" style={{ background: `linear-gradient(to bottom, transparent 40%, ${BG_CARD})` }} />
              </div>
              <div className="relative flex-1 space-y-4 p-7 sm:p-10">
                <Selo>Quem monta o seu cardápio</Selo>
                <h2 className="text-3xl font-extrabold tracking-[-0.02em] sm:text-4xl" style={{ color: TEXT_H }}>
                  Clara <span className="gradient-text">Camille</span>
                </h2>
                <p className="text-base leading-relaxed sm:text-lg" style={{ color: TEXT_B }}>
                  Nutricionista brasileira, com consultório em Hamilton (Brisbane) e pacientes em toda a Austrália. Conhece na prática o mercado daqui, o preço daqui e a rotina de quem veio para crescer, não para viver no improviso.
                </p>
                <p className="text-base leading-relaxed sm:text-lg" style={{ color: TEXT_B }}>
                  O clube é o sistema que ela monta no consultório, agora por um preço que cabe em qualquer semana.
                </p>
              </div>
            </div>
          </div>
          <div>
            <p className="mb-4 text-center text-[12px] font-bold uppercase tracking-wider" style={{ color: TEXT_M }}>
              Mensagens de pacientes da Clara
            </p>
            <div className="scrollbar-hide -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2">
              {DEPOIMENTOS.map((src, i) => (
                <img key={i} src={src} alt={`Depoimento de paciente ${i + 1}`} loading="lazy" className="h-72 w-auto flex-shrink-0 snap-center rounded-2xl border object-cover sm:h-80" style={{ borderColor: BORDER }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== OFERTA ========== */}
      <section id="vaga" className="scroll-mt-4 py-16 sm:py-24" style={{ background: BG_ALT }}>
        <div className="mx-auto max-w-5xl space-y-10 px-5">
          {VENDAS_ABERTAS ? (
            <>
              <Cabecalho
                selo={`${TRIAL_DIAS} dias grátis em todos`}
                titulo={
                  <>
                    Escolha o <span className="gradient-text italic">seu plano</span>
                  </>
                }
              />
              <div className="grid gap-5 sm:grid-cols-3 sm:gap-6">
                <PlanoCard id="mensal" destaque />
                <PlanoCard id="familia" />
                <PlanoCard id="anual" />
              </div>
              {FUNDADORAS_ABERTAS && (
                <div className="relative overflow-hidden rounded-3xl border p-6 sm:p-8" style={{ background: BG_CARD, borderColor: `hsl(50 70% 70% / 0.5)`, boxShadow: SOMBRA_CARD }}>
                  <div className="absolute -top-px left-0 right-0 h-1" style={{ background: `linear-gradient(90deg, ${ACCENT}, hsl(50 90% 60%), hsl(40 95% 55%), hsl(50 90% 60%), ${ACCENT})` }} />
                  <div className="flex flex-col items-center gap-5 sm:flex-row sm:gap-6">
                    <Sparkles className="h-8 w-8 flex-shrink-0" style={{ color: "hsl(45 80% 45%)" }} />
                    <div className="flex-1 space-y-1 text-center sm:text-left">
                      <p className="text-lg font-extrabold tracking-tight" style={{ color: TEXT_H }}>
                        {NOME_TURMA}: A$ {PLANOS.fundadora.preco} por mês, para sempre
                      </p>
                      <p className="text-sm" style={{ color: TEXT_B }}>
                        {VAGAS_FUNDADORAS} vagas, com os mesmos {TRIAL_DIAS} dias grátis. Quando acabarem, acabaram.
                      </p>
                    </div>
                    <button type="button" onClick={() => irCheckout("fundadora", "lp_fundadora")} className="rounded-2xl px-6 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white" style={{ background: GRADIENT }}>
                      Quero entrar na {NOME_TURMA}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <Cabecalho
                selo={`Lançamento em ${DATA_LANCAMENTO}`}
                titulo={
                  <>
                    {NOME_TURMA}: <span className="gradient-text italic">{VAGAS_FUNDADORAS} vagas</span>
                  </>
                }
                texto={`As ${VAGAS_FUNDADORAS} primeiras pessoas do clube pagam A$ ${PLANOS.fundadora.preco} por mês para sempre, em vez de A$ ${PLANOS.mensal.preco}. As vagas abrem primeiro para quem estiver na lista.`}
              />
              <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
                {/* Preço */}
                <div className="rounded-3xl border p-6 sm:p-8" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: SOMBRA_CARD }}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
                      {VAGAS_FUNDADORAS} vagas
                    </span>
                    <span className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ background: BG_ALT, color: TEXT_B }}>
                      {TRIAL_DIAS} dias grátis no lançamento
                    </span>
                  </div>
                  <div className="mt-5">
                    <p className="text-base font-semibold line-through" style={{ color: TEXT_M }}>
                      A$ {PLANOS.mensal.preco} por mês
                    </p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="whitespace-nowrap text-[56px] font-extrabold leading-none tracking-[-0.04em] sm:text-6xl" style={{ color: TEXT_H }}>
                        A$ {PLANOS.fundadora.preco}
                      </span>
                      <span className="text-base font-semibold" style={{ color: ACCENT }}>
                        por mês, para sempre
                      </span>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed" style={{ color: TEXT_B }}>
                    Enquanto você continuar assinando, o preço não muda. Cancela em um clique. Quando as {VAGAS_FUNDADORAS} vagas acabarem, o clube passa a custar A$ {PLANOS.mensal.preco} por mês.
                  </p>
                  <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                    {BENEFICIOS.map((b) => (
                      <li key={b.titulo} className="flex items-start gap-2.5 text-sm leading-snug" style={{ color: TEXT_B }}>
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: ACCENT }} strokeWidth={3} /> {b.titulo}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Formulário */}
                <div className="rounded-3xl border p-6 sm:p-8 lg:sticky lg:top-6" style={{ background: BG_CARD, borderColor: BORDER_ACCENT, boxShadow: SOMBRA_DESTAQUE }}>
                  <div className="mb-5 space-y-1.5 text-center">
                    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ background: `hsl(150 22% 35% / 0.1)`, color: ACCENT }}>
                      <Sparkles className="h-3.5 w-3.5" /> Lista VIP da {NOME_TURMA}
                    </span>
                    <h3 className="text-2xl font-extrabold tracking-tight" style={{ color: TEXT_H }}>
                      Garanta a sua vaga
                    </h3>
                    <p className="text-sm" style={{ color: TEXT_B }}>
                      Quem está na lista recebe o link primeiro, no WhatsApp, em {DATA_LANCAMENTO}.
                    </p>
                  </div>
                  <ListaVipForm origem="lp_clube" />
                </div>

                {/* Linha do tempo */}
                <div className="rounded-3xl border p-6 sm:p-8 lg:col-span-2" style={{ background: BG_CARD, borderColor: BORDER, boxShadow: SOMBRA_CARD }}>
                  <p className="mb-5 text-[12px] font-bold uppercase tracking-wider" style={{ color: TEXT_M }}>
                    Como vai ser
                  </p>
                  <ol className="grid gap-5 sm:grid-cols-3">
                    {[
                      ["Hoje", "Você entra na lista. É grátis e não é compra."],
                      [DATA_LANCAMENTO, `O link chega no seu WhatsApp, antes de todo mundo. Você começa com ${TRIAL_DIAS} dias grátis.`],
                      ["Primeira quarta", "Seu cardápio chega às 7h, nas ofertas do seu mercado e nas suas medidas."],
                    ].map(([q, d], i) => (
                      <li key={q} className="flex gap-3">
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-extrabold text-white" style={{ background: GRADIENT }}>
                          {i + 1}
                        </span>
                        <div>
                          <p className="text-sm font-extrabold" style={{ color: TEXT_H }}>
                            {q}
                          </p>
                          <p className="mt-0.5 text-sm leading-relaxed" style={{ color: TEXT_B }}>
                            {d}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-5 text-xs" style={{ color: TEXT_M }}>
                    Depois do lançamento: Mensal A$ {PLANOS.mensal.preco} · Família A$ {PLANOS.familia.preco} (até 4 perfis) · Anual A$ {PLANOS.anual.preco}.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ========== GARANTIA ========== */}
      <section className="py-12 sm:py-16" style={{ background: BG_PAGE }}>
        <div className="mx-auto max-w-3xl px-5">
          <div className="flex flex-col items-start gap-5 rounded-3xl border p-6 sm:flex-row sm:p-8" style={{ background: BG_CARD, borderColor: BORDER_ACCENT, boxShadow: `0 8px 32px hsl(30 10% 70% / 0.15)` }}>
            <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl" style={{ background: `hsl(150 22% 35% / 0.1)` }}>
              <ShieldCheck className="h-7 w-7" style={{ color: ACCENT }} />
            </span>
            <div className="space-y-2">
              <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: TEXT_H }}>
                Garantia sem letra miúda
              </h2>
              <p className="leading-relaxed" style={{ color: TEXT_B }}>
                {TRIAL_DIAS} dias grátis para ver o cardápio, a lista e a comunidade. Cancela em um clique, sem perguntas. No primeiro mês pago, devolução integral em até 30 dias.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FAQ ========== */}
      <section className="py-16 sm:py-24" style={{ background: BG_ALT }}>
        <div className="mx-auto max-w-3xl space-y-10 px-5">
          <Cabecalho
            selo="Dúvidas"
            titulo={
              <>
                Perguntas que <span className="gradient-text italic">todo mundo faz</span>
              </>
            }
          />
          <Faq />
        </div>
      </section>

      {/* ========== CTA FINAL ========== */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="absolute inset-0" style={{ background: GRADIENT }} />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${marmitas})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative z-10 mx-auto flex max-w-3xl justify-center px-5">
          <div className="w-full space-y-6 rounded-3xl px-6 py-10 text-center sm:px-12 sm:py-14" style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.3)" }}>
            <h2 className="text-3xl font-extrabold leading-tight tracking-[-0.02em] text-white sm:text-4xl">{VENDAS_ABERTAS ? "Na próxima quarta você já recebe o seu cardápio" : `Em ${DATA_LANCAMENTO}, as ${VAGAS_FUNDADORAS} vagas abrem primeiro para a lista`}</h2>
            <p className="text-base text-white/90 sm:text-lg">{VENDAS_ABERTAS ? `Entre hoje, use os ${TRIAL_DIAS} dias grátis e veja a economia aparecer na tela.` : `A$ ${PLANOS.fundadora.preco} por mês para sempre, ${TRIAL_DIAS} dias grátis no lançamento, e a economia aparecendo na tela toda quarta.`}</p>
            <div className="flex flex-col items-center gap-3">
              <button type="button" onClick={() => acaoPrincipal("lp_final")} className="w-full max-w-md rounded-2xl bg-white py-5 text-lg font-extrabold uppercase tracking-wide shadow-lg transition hover:scale-[1.02]" style={{ color: ACCENT }}>
                {CTA_TEXT}
              </button>
              <Link to="/diagnostico" onClick={irDiagnostico} className="text-sm font-semibold text-white underline underline-offset-4">
                Prefiro fazer o diagnóstico primeiro
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mini rodapé */}
      <div className="px-5 py-8 text-center text-xs" style={{ borderTop: `1px solid hsl(32 15% 88% / 0.5)`, color: TEXT_M, background: BG_ALT }}>
        <strong>Clara Camille {new Date().getFullYear()}</strong> · Nutricionista brasileira na Austrália · 8 Harbour Rd, Hamilton QLD 4007 ·{" "}
        <Link to="/politica-de-privacidade" className="underline underline-offset-2">
          Política de privacidade
        </Link>
      </div>
      <div className="h-16 lg:hidden" />

      <BarraFixaMobile visivel={barra} />
      {!VENDAS_ABERTAS && <FolhaVaga aberta={folha.aberta} origem={folha.origem} onFechar={fecharFolha} />}
    </div>
  );
};

export default Clube;
